/**
 * Create Project Page
 * @implements US-013 - Project Creation (Frontend)
 * @implements FS-002.1.1 - Create New Project
 * @implements FS-002.1.2 - Mandatory First Project (Onboarding)
 * @implements FS-002.1.3 - Project Fields
 * @implements BS-002.2 - Mandatory Project for Platform Access
 * @implements BS-002.3 - Project Title Uniqueness
 * @implements BS-002.4 - Description Length Limit
 *
 * This page handles project creation for Product Owners.
 * It is used in two contexts:
 * 1. Onboarding: /onboarding/create-project (first project after registration)
 * 2. Regular: /projects/new (creating additional projects)
 *
 * Flow:
 * 1. User enters project title (required)
 * 2. User optionally enters project description (max 256 chars)
 * 3. User submits form
 * 4. On success, user is redirected to /projects/:id
 */

import { useState, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';

/**
 * Maximum description length
 * @implements BS-002.4 - Description Length Limit
 */
const MAX_DESCRIPTION_LENGTH = 256;

/**
 * GraphQL mutation for creating a project
 * @implements FS-002.1.1 - Create New Project
 *
 * Note: This is defined inline until codegen is run.
 * After running `yarn codegen`, use the generated useCreateProjectMutation hook.
 */
const CREATE_PROJECT_MUTATION = gql`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
      title
      description
      lifecycleState
      controlState
      createdAt
    }
  }
`;

/**
 * Input type for CreateProject mutation
 */
interface CreateProjectInput {
  title: string;
  description?: string;
}

/**
 * Response type for CreateProject mutation
 */
interface CreateProjectResponse {
  createProject: {
    id: string;
    title: string;
    description: string | null;
    lifecycleState: string;
    controlState: string;
    createdAt: string;
  };
}

/**
 * Extract error code from GraphQL error
 * @implements BS-002.3 - Project Title Uniqueness (DUPLICATE_TITLE error)
 */
function getErrorMessage(error: Error): string {
  // Check for GraphQL error extensions
  const graphQLError = error as {
    graphQLErrors?: Array<{ extensions?: { code?: string } }>;
  };
  if (graphQLError.graphQLErrors?.length) {
    const firstError = graphQLError.graphQLErrors[0];
    if (firstError.extensions?.code === 'DUPLICATE_TITLE') {
      return 'A project with this title already exists';
    }
  }
  return error.message || 'Failed to create project. Please try again.';
}

/**
 * CreateProjectPage - Project creation form
 *
 * @implements US-013 - Project Creation (Frontend)
 * @implements FS-002.1.1 - Create New Project
 * @implements FS-002.1.2 - Mandatory First Project (Onboarding)
 *
 * Routes:
 * - /onboarding/create-project (onboarding context)
 * - /projects/new (regular context)
 */
export const CreateProjectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this is the onboarding flow
  const isOnboarding = location.pathname.includes('/onboarding/');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);

  // GraphQL mutation
  const [createProject, { loading, error }] = useMutation<
    CreateProjectResponse,
    { input: CreateProjectInput }
  >(CREATE_PROJECT_MUTATION);

  /**
   * Handle title change
   */
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    // Clear error when user types
    if (titleError) {
      setTitleError(null);
    }
  };

  /**
   * Handle description change with character limit
   * @implements BS-002.4 - Description Length Limit
   */
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    // Only update if within limit
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(value);
    }
  };

  /**
   * Handle form submission
   * @implements FS-002.1.1 - Create New Project
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTitleError(null);

    // Validate title
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('Project title is required');
      return;
    }

    try {
      const result = await createProject({
        variables: {
          input: {
            title: trimmedTitle,
            description: description.trim() || undefined,
          },
        },
      });

      if (result.data?.createProject) {
        // Navigate to the project view page
        navigate(`/projects/${result.data.createProject.id}`);
      }
    } catch (err) {
      // Error handling is done via the error state from useMutation
      console.error('Failed to create project:', err);
    }
  };

  // Get display error message
  const displayError = error ? getErrorMessage(error) : null;

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {isOnboarding ? 'Create Your First Project' : 'Create New Project'}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            {isOnboarding
              ? 'Give your project a name to get started with the AI PM'
              : 'Set up a new project to start planning with the AI PM'}
          </Typography>

          {/* Project Creation Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Title Field (required) */}
            <TextField
              fullWidth
              label="Project Title"
              value={title}
              onChange={handleTitleChange}
              required
              error={!!titleError}
              helperText={
                titleError || 'Choose a descriptive name for your project'
              }
              placeholder="e.g., Mobile App, E-commerce Platform"
              sx={{ mb: 3 }}
              autoFocus
              inputProps={{
                'aria-describedby': 'title-helper-text',
              }}
            />

            {/* Description Field (optional with counter) */}
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={handleDescriptionChange}
              multiline
              rows={4}
              placeholder="Optional: Describe what this project is about"
              helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH} characters`}
              sx={{ mb: 3 }}
              inputProps={{
                'aria-describedby': 'description-helper-text',
                maxLength: MAX_DESCRIPTION_LENGTH,
              }}
            />

            {/* Error Alert */}
            {displayError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {displayError}
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Creating Project...
                </>
              ) : (
                'Create Project'
              )}
            </Button>

            {/* Cancel Link - only show for non-onboarding */}
            {!isOnboarding && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  onClick={() => navigate(-1)}
                  color="primary"
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
